import React, { Suspense } from 'react';
import { getModuleEntry } from '../../config/moduleRegistry';

// PanelHost — the generic adapter that replaces all 20 hand-written
// `{activeTab === 'x' && <Component .../>}` blocks that used to live in
// Dashboard.js. Looks up a panel's moduleKey in the module registry and
// renders the right component with the right props, based on that entry's
// needsUser/supportsFocus/pollingAware/isNotificationsPanel flags.
//
// `panel.focus` is scoped to THIS panel instance (set by usePanelManager's
// focusPanel), not a single global value — so two independent Room
// Management panels (e.g. via "+ New Window") each get their own focus
// targeting with no cross-talk.
function PanelHost({ panel, user, active, onFocusPanel }) {
  const entry = getModuleEntry(panel.moduleKey);
  if (!entry) return null; // defensive: a stale persisted moduleKey no longer in the registry — usePanelManager already filters this on load, cheap to double-guard here too

  const Component = entry.component;
  const props = {};
  if (entry.needsUser) props.user = user;
  if (entry.supportsFocus) props.focus = panel.focus;
  if (entry.pollingAware) props.active = active;
  // Notifications is itself openable as a panel, but its own row-click
  // needs to deep-link into ANOTHER module's panel — same (tab, entity)
  // => void signature focusPanel/openFromNotification already used
  // pre-MDI, so no change needed inside Notifications.js itself.
  if (entry.isNotificationsPanel) props.onOpen = onFocusPanel;
  // needsFocusPanel (2026-10-02) — same deep-link need as Notifications,
  // for a module whose own content wants to jump straight into ANOTHER
  // panel (Kitchen Sheet's "N tabs still open"/MUM/Director's Account
  // lines deep-linking into the matching Tables tab). Named onOpenTab to
  // match the pre-existing Dashboard.js TodaySpendCard convention
  // (onOpenTab={focusPanel}) rather than introducing a second name for
  // the same shape.
  if (entry.needsFocusPanel) props.onOpenTab = onFocusPanel;

  // The screen's code loads the first time it is opened — a brief, quiet
  // placeholder while it arrives (see lib/lazyWithRetry.js).
  return (
    <Suspense fallback={<div className="p-6 text-sm text-gray-600">Loading…</div>}>
      <Component {...props} />
    </Suspense>
  );
}

export default PanelHost;
